// componentes/shared/NavBar/Navbar.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import styles from './Navbar.module.css';
import { API_URL } from '../../config';
import { KNOWN_ROUTES } from '../../routesConfig';

function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isChatbotPage = location.pathname === "/chatbot";
  const isPanelAdmin = location.pathname.startsWith("/panel-admin")
    || location.pathname === "/confirmar-correo"
    || !KNOWN_ROUTES.includes(location.pathname);
  const username = localStorage.getItem('username'); // Recupera el nombre de usuario

  const [showWelcome, setShowWelcome] = useState(false);
  const [clientsCount, setClientsCount] = useState(500);
  const [pendingScroll, setPendingScroll] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  // Reintenta encontrar la sección hasta 2s (los componentes de la home cargan con lazy())
  const scrollToSection = useCallback((sectionId, attemptsLeft = 20) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else if (attemptsLeft > 0) {
      setTimeout(() => scrollToSection(sectionId, attemptsLeft - 1), 100);
    }
  }, []);

  useEffect(() => {
    if (pendingScroll && location.pathname === '/') {
      scrollToSection(pendingScroll);
      setPendingScroll(null);
    }
  }, [pendingScroll, location.pathname, scrollToSection]);

  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      setPendingScroll(sectionId);
      navigate('/');
    }
  };

  // Cierra el menú móvil al cambiar de ruta
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Cierra el menú móvil al hacer clic fuera de él
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    let isMounted = true;

    fetch(`${API_URL}/clients-count/`)
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted || typeof data.count !== 'number') return;

        const start = 500;
        const target = data.count;
        const duration = 800;
        const startTime = performance.now();

        const animate = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          setClientsCount(Math.round(start + (target - start) * progress));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      })
      .catch(() => {
        // Si falla la petición, se conserva el valor base (500)
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const shouldShowWelcome = localStorage.getItem('showWelcomeMessage') === 'true';
    if (shouldShowWelcome && username) {
      setShowWelcome(true);
      // Desactivar la bandera para que no se muestre nuevamente
      localStorage.setItem('showWelcomeMessage', 'false');

      // Ocultar el mensaje después de 5 segundos
      const timer = setTimeout(() => {
        setShowWelcome(false);
      }, 5000);

      // Limpiar el temporizador si el componente se desmonta
      return () => clearTimeout(timer);
    }
  }, [username]);

  // El panel de administrador tiene su propio layout (sidebar con logo/menú/logout)
  if (isPanelAdmin) return null;

  return (
    <>
    <nav className={styles.navegacion} ref={navRef}>
      {!isChatbotPage && (
        <>
          <div className={styles.logoContainer}>
            <Link to="/">
              <img src="/img/logo sin fondo.png" alt="Kredilibranza Logo" className={styles.logo} />
            </Link>
          </div>

          <button
            type="button"
            className={styles.hamburgerButton}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>

          <ul className={`${styles.menu} ${menuOpen ? styles.menuOpen : ''}`}>
            <li className={styles.menuItem}>
              <a href="#condiciones" className={styles.menuLink} onClick={(e) => handleSectionClick(e, 'condiciones')}>Condiciones</a>
            </li>
            <li className={styles.menuItem}>
              <a href="#simulador" className={styles.menuLink} onClick={(e) => handleSectionClick(e, 'simulador')}>Simulador</a>
            </li>
            <li className={styles.menuItem}>
              <a href="#quienes-somos" className={styles.menuLink} onClick={(e) => handleSectionClick(e, 'quienes-somos')}>¿Quiénes Somos?</a>
            </li>
            <li className={styles.menuItem}>
              <a href="#contenedor-contactos" className={styles.menuLink} onClick={(e) => handleSectionClick(e, 'contenedor-contactos')}>Contáctanos</a>
            </li>
          </ul>

          {/* Mensaje de Bienvenida */}
          {showWelcome && (
            <div className={styles.welcomeCard}>
              <p>Bienvenido {username}</p>
            </div>
          )}
        </>
      )}
      {isChatbotPage && (
        <ul className={styles.menu}>
          <li className={styles.menuItem}>
            <Link to="/" className={styles.menuLink}>Volver</Link>
          </li>
        </ul>
      )}
    </nav>
    {!isChatbotPage && (
      <div className={styles.clientBadge}>
        <img src="/img/clientes.png" alt="Clientes" className={styles.clientes} />
        <span className={styles.txtClientes}>Clientes</span>
        <strong className={styles.numClientes}>{clientsCount.toLocaleString('es-CO')}</strong>
      </div>
    )}
    </>
  );
}

export default NavBar;
